<template>
  <BasicButton
    v-if="mode === 'upload'"
    label="Déposer"
    size="small"
    type="secondary"
    variant="outlined"
    iconName="document-download"
    @click="showUploadFilesModal()"
    v-bind="$attrs"
  />
  <div
    v-else
    class="documentsWrapper"
    v-bind="$attrs"
  >
    <div class="sectionHeader">
      <span class="sectionTitle">Pièces justificatives ({{ pieces.length }})</span>
      <BasicButton
        label="Déposer"
        size="small"
        type="secondary"
        variant="outlined"
        iconName="document-download"
        @click="showUploadFilesModal()"
      />
    </div>
    <div class="documentsGridWrapper">
      <div class="cardLayoutWrapper cardLayoutWrapper--header">
        <div class="elem elem--span-8">
          <span>Conformité</span>
        </div>
        <div class="elem elem--span-18">
          <span>Nature du document</span>
        </div>
        <div class="elem elem--span-4"></div>
      </div>
      <div
        class="gridElemWrapper"
        v-for="(elem, index) in pieces"
        :key="index"
      >
        <div class="cardLayoutWrapper">
          <div class="elem elem--span-8">
            <div class="elem--column">
              <BasicTag
                v-if="elem.statut != 'EnAttenteDeTraitementParAkto'"
                size="medium"
                :label="conformiteToTagLabel(elem.conformite)"
              />
              <BasicTag
                v-if="elem.statut != null"
                size="medium"
                :label="statutToTagLabel(elem.statut)"
              />
            </div>
          </div>
          <div class="elem elem--span-18 elem--bold">
            <span>{{ elem.nature }}</span>
          </div>
          <div class="elem elem--span-4"></div>
          <div class="elem elem--span-4">
            <BasicButton
              label="Déposer"
              size="small"
              type="secondary"
              variant="outlined"
              iconName="document-download"
              @click="showUploadFilesModal(elem)"
            />
          </div>
        </div>
      </div>
      <span
        v-if="!pieces.length"
        class="emptyListPlaceholder"
      >
        Aucun élément
      </span>
    </div>
    <div class="documentsMobileWrapper">
      <div
        class="documentsMobileElemWrapper"
        v-for="(elem, index) in piecesList"
        :key="index"
      >
        <div class="headerWrapper">
          <div class="leftContent">
            <span class="subtitle">{{ elem.nature }}</span>
            <BasicTag
              v-if="elem.statut != 'EnAttenteDeTraitementParAkto'"
              size="medium"
              :label="conformiteToTagLabel(elem.conformite)"
            />
            <BasicTag
              v-if="elem.statut != null"
              size="medium"
              :label="statutToTagLabel(elem.statut)"
            />
          </div>
          <div class="rightContent">
            <BasicButton
              label="Déposer"
              size="small"
              type="secondary"
              variant="outlined"
              iconName="document-download"
              @click="showUploadFilesModal(elem)"
            />
          </div>
        </div>
      </div>
      <BasicButton
        v-if="pieces.length! > 2"
        @click="showFullPiecesList = !showFullPiecesList"
        variant="outlined"
        :label="!showFullPiecesList ? 'Déployer la liste' : 'Réduire la liste'"
        iconRight
        :iconName="!showFullPiecesList ? 'arrow-down' : 'arrow-up'"
      />
      <span
        v-if="!pieces.length"
        class="emptyListPlaceholder"
      >
        Aucun élément
      </span>
    </div>
  </div>
  <UploadFiles
    v-model="showUploadFilesModalVisible"
    :code="code"
    :endpoint="endpoint"
    :fichier
    :dispositif
    :natures-authorisees
    @upload-success="emits('upload-success', $event)"
  />
</template>

<script setup lang="ts">
  import { StatutFactureDTO } from '@/features/api/extranet/dto/Facture/StatutFactureDTO'
import type { PieceDTO } from '@/features/api/extranet/dto/shared/PieceDTO'
import type { Dispositif } from '@/features/domaine/types/opco/shared/Dispositif'
import { useDialog } from '@/features/interface/dialog'
import { computed, ref } from 'vue'
import { DossierStatusPreventUpload, FactureStatusPreventUpload } from './Documents.types'
import type { Endpoint } from './UploadFiles.types'
import UploadFiles from './UploadFiles.vue'

  defineOptions({
    inheritAttrs: false,
  })

  const fichier = ref<PieceDTO>()
  const showUploadFilesModalVisible = ref(false)

  const props = defineProps<{
    pieces: PieceDTO[]
    dispositif: Dispositif | Dispositif[]
    code: string
    endpoint: Endpoint
    statut: StatutFactureDTO | string
    naturesAuthorisees?: string[]
    mode?: 'upload' | 'view'
  }>()

  const emits = defineEmits<{
    'upload-success': [count: number]
  }>()

  const showFullPiecesList = ref(false)

  const piecesList = computed(() => {
    if (props.pieces.length! > 2 && !showFullPiecesList.value) {
      return props.pieces.slice(0, 2)
    }
    return props.pieces
  })

  const conformiteToTagLabel = (conformite: any) => {
    switch (conformite) {
      case 'Conforme':
        return 'CONFORME'
      case 'NonConforme':
        return 'NON CONFORME'
      case 'EnAttente':
        return 'EN ATTENTE'
      case 'EnCoursDAnalyse':
        return "EN COURS D'ANALYSE"
      default:
        return ''
    }
  }

  const statutToTagLabel = (statut: any) => {
    switch (statut) {
      case 'EnAttenteDeTraitementParAkto':
        return 'EN ATTENTE DE TRAITEMENT PAR AKTO'
      case 'EnAttenteDeDepot':
        return 'EN ATTENTE DE DEPOT'
      case 'Manquant':
        return 'MANQUANT'
      case 'Recu':
        return 'REÇU'
      default:
        return ''
    }
  }

  const statutPreventsUploadModal = () => {
    if (props.endpoint === 'dossiers' || props.endpoint === 'outilsfpe') {
      return DossierStatusPreventUpload.includes(props.statut as any)
    } else if (props.endpoint === 'factures') {
      return FactureStatusPreventUpload.includes(props.statut as any)
    }
    return false
  }

  const showImpossibleUploadDialog = async () => {
    const dialog = useDialog()
    await dialog.showDialog({
      type: 'Error',
      title: 'Information',
      message:
        props.endpoint === 'dossiers' || props.endpoint === 'outilsfpe'
          ? 'Le statut du dossier ne permet plus de déposer un document.'
          : 'Le statut de la DR/facture ne permet plus de déposer un document',
    })
  }

  const showUploadFilesModal = (elem?: PieceDTO) => {
    if (statutPreventsUploadModal()) {
      showImpossibleUploadDialog()
    } else {
      fichier.value = elem
      showUploadFilesModalVisible.value = true
    }
  }
</script>

<style scoped lang="less">
  @import '../index.less';
</style>
